/**
 * Meta Pixel loader — RGPD-compliant (only loads after consent).
 * Mirror du pattern GA4 dans use-cookie-consent.ts.
 *
 * Activation : définir `VITE_META_PIXEL_ID` dans `.env.local` ou `.env.production`.
 * Sans cette variable, toutes les fonctions exportées sont des no-op.
 *
 * Events trackés :
 *  - PageView  → automatique au load
 *  - ViewContent → trackMetaPixelEvent('ViewContent', { content_name: '/tarifs' })
 *  - Lead → trackMetaPixelEvent('Lead', { value, currency })
 *  - CompleteRegistration → trackMetaPixelEvent('CompleteRegistration')
 */

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const SCRIPT_SELECTOR = 'script[data-meta-pixel="velko"]';

type FbqFunction = ((...args: unknown[]) => void) & {
  queue?: unknown[];
  callMethod?: (...args: unknown[]) => void;
  push?: (...args: unknown[]) => void;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

export function loadMetaPixel(): void {
  if (!PIXEL_ID) return;
  if (typeof window === 'undefined') return;
  if (document.querySelector(SCRIPT_SELECTOR)) return;
  if (window.fbq?.loaded) return;

  // Function stub (Meta official pattern, GDPR-safe — no network yet).
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  } as FbqFunction;
  fbq.push = fbq;
  fbq.loaded = false;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = window.fbq ?? fbq;
  window._fbq = window._fbq ?? fbq;

  // Inject the actual script (this is what makes the network calls).
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.dataset.metaPixel = 'velko';
  document.head.appendChild(script);

  // Initialize + PageView.
  window.fbq?.('init', PIXEL_ID);
  window.fbq?.('track', 'PageView');
}

export function removeMetaPixel(): void {
  if (typeof window === 'undefined') return;

  document.querySelector(SCRIPT_SELECTOR)?.remove();

  // Clear Meta cookies (set on .facebook.com but mirrored on this domain).
  document.cookie.split(';').forEach((c) => {
    const name = c.trim().split('=')[0];
    if (name?.startsWith('_fbp') || name?.startsWith('_fbc')) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });

  delete window.fbq;
  delete window._fbq;
}

let lastSpaPath: string | null = null;

/**
 * PageView SPA — à brancher sur `router.subscribe()` (voir main.tsx).
 * Le premier appel fixe la baseline sans tracker : le PageView initial est déjà
 * émis par `loadMetaPixel()` au moment du consentement. Les navigations suivantes
 * ne trackent qu'en cas de changement réel de pathname (ignore les re-renders et
 * les changements de query/hash).
 */
export function trackSpaPageView(pathname: string): void {
  if (!PIXEL_ID) return;
  if (typeof window === 'undefined') return;
  if (lastSpaPath === pathname) return;
  const isFirstCall = lastSpaPath === null;
  lastSpaPath = pathname;
  if (isFirstCall) return;
  window.fbq?.('track', 'PageView');
}

type MetaPixelEvent =
  | 'PageView'
  | 'ViewContent'
  | 'Lead'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'Purchase'
  | 'Contact';

export function trackMetaPixelEvent(
  event: MetaPixelEvent,
  parameters?: Record<string, unknown>,
): void {
  if (!PIXEL_ID) return;
  if (typeof window === 'undefined') return;
  window.fbq?.('track', event, parameters);
}
